import { dkoBucketLabel } from './bradley-citations.js'

export const BRADLEY_SOURCE_TYPE = {
  GENIE: 'genie',
  DSS_SHAREPOINT: 'dss',
  DKO: 'dko',
  USER_UPLOAD: 'user_upload_file',
}

export function isAssistantMessage(message) {
  if (!message) {
    return false
  }

  const value = message.is_user
  return value === false || value === 0 || value === 'false'
}

export function isUserMessage(message) {
  if (!message) {
    return false
  }

  const value = message.is_user
  return value === true || value === 1 || value === 'true'
}

function extractFileName(url) {
  if (!url) {
    return 'Document'
  }

  const parts = url.split('/')
  const fileNameWithExtension = parts[parts.length - 1]
  const fileName = fileNameWithExtension.replace(/\.[^/.]+$/, '')
  const decodedFileName = decodeURIComponent(fileName)

  return decodedFileName || 'Document'
}

function getDocumentTitle(document_) {
  const title = document_?.doc_title
    ?? document_?.metadata?.doc_title
    ?? document_?.file_name
    ?? document_?.metadata?.file_name
    ?? document_?.metadata?.title
  if (typeof title === 'string' && title.trim() !== '') {
    return title.trim()
  }

  return extractFileName(document_?.share_point_url ?? document_?.metadata?.share_point_url)
}

function getDocumentDkoType(document_) {
  return (
    document_?.dko_document_type
    ?? document_?.metadata?.dko_document_type
    ?? document_?.document_type
    ?? document_?.metadata?.document_type
    ?? ''
  )
}

function getDocumentSimilarity(document_) {
  const score = document_?.similarity_score ?? document_?.metadata?.similarity_score
  return typeof score === 'number' ? score : null
}

function resolveSourceIndex(document_) {
  const sourceIndex = document_?.source_index ?? document_?.metadata?.source_index

  if (
    sourceIndex === BRADLEY_SOURCE_TYPE.DSS_SHAREPOINT
    || sourceIndex === BRADLEY_SOURCE_TYPE.DKO
  ) {
    return BRADLEY_SOURCE_TYPE.DSS_SHAREPOINT
  }

  if (sourceIndex === BRADLEY_SOURCE_TYPE.USER_UPLOAD) {
    return BRADLEY_SOURCE_TYPE.USER_UPLOAD
  }

  return null
}

function normalizeSelectedDocumentsMetadata(metadata) {
  if (!Array.isArray(metadata)) {
    return []
  }

  return metadata
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const fileName = item.file_name ?? item.name ?? item.doc_title
      const fileHash = item.file_hash ?? item.id ?? item.document_id
      const fileId = item.file_id

      if (!fileName && !fileHash) {
        return null
      }

      return {
        file_name: fileName,
        file_hash: fileHash,
        ...(fileId ? { file_id: fileId } : {}),
      }
    })
    .filter(Boolean)
}

export function findPrecedingUserMessage(assistantMessage, conversationMessages = []) {
  if (!assistantMessage || !Array.isArray(conversationMessages)) {
    return null
  }

  const assistantId = assistantMessage.message_id
  let assistantIndex = assistantId
    ? conversationMessages.findIndex(message => message?.message_id === assistantId)
    : -1

  if (assistantIndex === -1) {
    assistantIndex = conversationMessages.indexOf(assistantMessage)
  }

  if (assistantIndex <= 0) {
    return null
  }

  for (let index = assistantIndex - 1; index >= 0; index -= 1) {
    if (isUserMessage(conversationMessages[index])) {
      return conversationMessages[index]
    }
  }

  return null
}

function normalizeUploadTitle(title) {
  return typeof title === 'string' ? title.trim().toLowerCase() : ''
}

function stripUploadFileExtension(fileName) {
  return normalizeUploadTitle(fileName).replace(/\.[^/.]+$/, '')
}

function titlesReferToSameUpload(existingTitle, candidateTitle) {
  const left = normalizeUploadTitle(existingTitle)
  const right = normalizeUploadTitle(candidateTitle)

  if (!left || !right) {
    return false
  }

  if (left === right) {
    return true
  }

  const leftBase = stripUploadFileExtension(left)
  const rightBase = stripUploadFileExtension(right)

  return left === rightBase || right === leftBase
}

function getExistingUserUploads(documents) {
  if (!Array.isArray(documents)) {
    return []
  }

  return documents.filter(document_ => resolveSourceIndex(document_) === BRADLEY_SOURCE_TYPE.USER_UPLOAD)
}

function buildSyntheticUserUploadDocuments(selectedMetadata, existingDocuments) {
  const existingUserUploads = getExistingUserUploads(existingDocuments)
  const existingHashes = new Set(
    existingUserUploads
      .map(document_ => document_?.file_hash ?? document_?.metadata?.file_hash)
      .filter(Boolean)
      .map(String),
  )

  return normalizeSelectedDocumentsMetadata(selectedMetadata)
    .filter((item) => {
      if (item.file_hash && existingHashes.has(String(item.file_hash))) {
        return false
      }

      return !existingUserUploads.some(document_ => (
        titlesReferToSameUpload(getDocumentTitle(document_), item.file_name)
      ))
    })
    .map((item, index) => ({
      source_index: BRADLEY_SOURCE_TYPE.USER_UPLOAD,
      doc_title: item.file_name,
      file_hash: item.file_hash,
      file_id: item.file_id,
      reference_id: item.file_id || item.file_hash || `user-upload-${index}`,
    }))
}

export function enrichAssistantMessageWithUserProvidedDocuments(message, conversationMessages = []) {
  if (!isAssistantMessage(message)) {
    return message
  }

  const documents = Array.isArray(message.documents) ? message.documents : []
  const attachedMetadata = message.selected_documents_metadata
    ?? findPrecedingUserMessage(message, conversationMessages)?.selected_documents_metadata
    ?? []
  const syntheticDocuments = buildSyntheticUserUploadDocuments(attachedMetadata, documents)

  if (syntheticDocuments.length === 0) {
    return message
  }

  return {
    ...message,
    documents: [...documents, ...syntheticDocuments],
  }
}

function getDocumentFileId(document_) {
  return document_?.file_id
    ?? document_?.metadata?.file_id
    ?? ''
}

function getDocumentFileHash(document_) {
  return document_?.file_hash
    ?? document_?.metadata?.file_hash
    ?? ''
}

function buildDocumentSource(document_, index) {
  const sourceIndex = resolveSourceIndex(document_)
  const documentTitle = getDocumentTitle(document_)
  const sharePointUrl = document_?.share_point_url ?? document_?.metadata?.share_point_url ?? null
  const similarity = getDocumentSimilarity(document_)
  const id = document_?.reference_id ?? document_?.metadata?.id ?? document_?.file_hash ?? `document-${index}`
  const markers = document_?.markers ?? document_?.metadata?.markers ?? []
  const kindLabel = dkoBucketLabel(getDocumentDkoType(document_))
  const fileId = getDocumentFileId(document_)
  const fileHash = getDocumentFileHash(document_)

  if (sourceIndex === BRADLEY_SOURCE_TYPE.DSS_SHAREPOINT) {
    return {
      id,
      type: BRADLEY_SOURCE_TYPE.DSS_SHAREPOINT,
      title: documentTitle,
      name: documentTitle,
      label: `dss + Sharepoint${documentTitle}`,
      url: sharePointUrl,
      similarity,
      markers,
      kindLabel,
    }
  }

  if (sourceIndex === BRADLEY_SOURCE_TYPE.USER_UPLOAD) {
    return {
      id,
      type: BRADLEY_SOURCE_TYPE.USER_UPLOAD,
      title: documentTitle,
      name: documentTitle,
      label: `User Upload${documentTitle}`,
      markers,
      url: document_?.url ?? document_?.metadata?.url ?? null,
      similarity,
      kindLabel,
      fileId,
      fileHash,
    }
  }

  return null
}

export function buildBradleySourcesFromMessage(message, conversationMessages = [], uploadedDocuments = []) {
  const enrichedMessage = enrichAssistantMessageWithUserProvidedDocuments(message, conversationMessages)

  if (!isAssistantMessage(enrichedMessage)) {
    return []
  }

  const sources = []

  if (enrichedMessage.agent_type === BRADLEY_SOURCE_TYPE.GENIE) {
    sources.push({
      id: 'genie',
      type: BRADLEY_SOURCE_TYPE.GENIE,
      title: enrichedMessage.query || 'dss + 360 Data',
      name: 'dss + 360 Data',
      label: 'dss + 360 Data',
      url: null,
      similarity: null,
    })
  }

  const documents = Array.isArray(enrichedMessage.documents) ? enrichedMessage.documents : []
  const documentSources = []
  for (const [index, document_] of documents.entries()) {
    const source = buildDocumentSource(document_, index)
    if (source) {
      documentSources.push({ ...source, order: index })
    }
  }

  documentSources.sort((left, right) => {
    // By citation marker, so the list reads in the order the answer cites it. Sources the
    // answer never cited keep their retrieval order, after the cited ones.
    const leftMarker = Math.min(...(left.markers?.length ? left.markers : [Number.POSITIVE_INFINITY]))
    const rightMarker = Math.min(...(right.markers?.length ? right.markers : [Number.POSITIVE_INFINITY]))
    return leftMarker - rightMarker || left.order - right.order
  })

  sources.push(...documentSources.map(({ order, ...source }) => source))

  return sources.map(source => attachUploadFileId(source, conversationMessages, uploadedDocuments))
}

function collectKnownUploads(conversationMessages, uploadedDocuments) {
  const known = []
  for (const document_ of uploadedDocuments ?? []) {
    if (document_) {
      known.push(document_)
    }
  }
  for (const message of conversationMessages ?? []) {
    for (const item of message?.selected_documents_metadata ?? []) {
      if (item) {
        known.push(item)
      }
    }
  }
  return known
}

function attachUploadFileId(source, conversationMessages, uploadedDocuments) {
  if (source?.type !== BRADLEY_SOURCE_TYPE.USER_UPLOAD || source.fileId) {
    return source
  }

  const match = collectKnownUploads(conversationMessages, uploadedDocuments).find((item) => {
    const fileId = item.file_id ?? item.fileId
    if (!fileId) {
      return false
    }
    const hash = item.file_hash ?? item.fileHash
    if (source.fileHash && hash && String(hash) === String(source.fileHash)) {
      return true
    }
    const name = item.file_name ?? item.doc_title ?? item.title
    return name && titlesReferToSameUpload(source.title, name)
  })

  if (!match) {
    return source
  }

  return {
    ...source,
    fileId: match.file_id ?? match.fileId,
  }
}

export function messageHasBradleySources(message, conversationMessages = []) {
  return buildBradleySourcesFromMessage(message, conversationMessages).length > 0
}
